import { useState, type ReactNode } from 'react';
import { Package } from 'lucide-react';
import {
  Card,
  CardGridSkeleton,
  EmptyState,
  PageHeader,
  PlatformIcon,
  TableSkeleton,
  ViewSwitch,
} from '@/components/ui';
import { ErrorState } from '@/components/ErrorState';
import { BulkRunDrawer } from '@/components/bulk/BulkRunDrawer';
import { NewSiteWizard } from '@/components/sites/NewSiteWizard';
import { TemplateFamilyDrawer } from '@/components/templates/TemplateFamilyDrawer';
import { useRoles } from '@/hooks/useAuth';
import { useTemplateFamilies } from '@/hooks/useTemplates';
import { useListView } from '@/store/preferencesStore';
import { formatDateTime, formatRelative, plural } from '@/utils/format';
import { can } from '@/utils/permissions';
import type { TemplateFamilyView } from '@/types/api';

/**
 * Templates, one per name (ADR-0047): a build carries a theme, plugins and a
 * structure and makes new GEOs; an image-only one pins WordPress and PHP. A
 * template opens into its versions, its reference site and its sites.
 */
export function TemplatesPage(): ReactNode {
  const roles = useRoles();
  const [openId, setOpenId] = useState<string | null>(null);
  const [geoFor, setGeoFor] = useState<string | null>(null);
  const [run, setRun] = useState<string | null>(null);
  const [view, setView] = useListView('templates');
  const families = useTemplateFamilies();
  const items = families.data ?? [];
  const open = items.find((family) => family.id === openId) ?? null;

  return (
    <div className="page">
      <PageHeader title="Шаблоны" subtitle="Основа для новых сайтов и их общая структура" actions={<ViewSwitch value={view} onChange={setView} />} />

      {view === 'cards' && families.isPending ? (
        <CardGridSkeleton cards={3} />
      ) : view === 'cards' && items.length > 0 ? (
        <div className="card-grid">
          {items.map((family) => (
            <FamilyCard key={family.id} family={family} onOpen={() => setOpenId(family.id)} />
          ))}
        </div>
      ) : (
        <Card flush>
          {families.isPending ? (
            <TableSkeleton rows={4} columns={5} />
          ) : families.isError ? (
            <ErrorState error={families.error} />
          ) : items.length === 0 ? (
            <EmptyState icon={Package} message="Шаблонов пока нет. Шаблоном можно сделать готовый сайт: «Ещё» → «Сделать шаблоном»" />
          ) : (
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th scope="col">Шаблон</th>
                    <th scope="col">Версия</th>
                    <th scope="col" className="numeric">
                      Сайтов
                    </th>
                    <th scope="col">Эталон</th>
                    <th scope="col">Опубликована</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((family) => (
                    <tr
                      key={family.id}
                      className="row-link"
                      tabIndex={0}
                      onClick={() => setOpenId(family.id)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') setOpenId(family.id);
                      }}
                    >
                      <td>
                        <span className="platform-name">
                          <PlatformIcon platform="wordpress" />
                          <span className="strong">{family.name}</span>
                          {family.latest.build ? null : <span className="tag">образ</span>}
                        </span>
                      </td>
                      <td className="mono nowrap">{family.latest.version}</td>
                      <td className="numeric">
                        {family.siteCount}
                        {family.outdatedCount > 0 ? <span className="tone-degraded text-sm"> · {family.outdatedCount} на старых</span> : null}
                      </td>
                      <td className="muted">{family.referenceSite?.name ?? '—'}</td>
                      <td className="muted nowrap" title={formatDateTime(family.latest.createdAt)}>
                        {formatRelative(family.latest.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      <TemplateFamilyDrawer
        family={open}
        onClose={() => setOpenId(null)}
        onNewGeo={(templateId) => {
          setOpenId(null);
          setGeoFor(templateId);
        }}
        onRolledOut={(runId) => {
          setOpenId(null);
          setRun(runId);
        }}
        mayManage={can(roles, 'templates.manage')}
        mayDeploy={can(roles, 'sites.deploy')}
        mayCreate={can(roles, 'sites.create')}
      />
      <NewSiteWizard open={geoFor !== null} templateId={geoFor} onClose={() => setGeoFor(null)} />
      <BulkRunDrawer id={run} onClose={() => setRun(null)} />
    </div>
  );
}

/** A template as a card: its look first, then its name, its version and how many sites are behind. */
function FamilyCard({ family, onOpen }: { readonly family: TemplateFamilyView; readonly onOpen: () => void }): ReactNode {
  return (
    <article className="picture-card">
      <div className="picture-card-shot">
        {family.preview ? (
          <img src={`/api/v1/sites/${family.preview.siteId}/preview?t=${encodeURIComponent(family.preview.takenAt)}`} alt="" loading="lazy" decoding="async" />
        ) : (
          <div className="picture-card-placeholder">
            <PlatformIcon platform="wordpress" size={40} />
            <span>Снимка ещё нет</span>
          </div>
        )}
      </div>
      <div className="picture-card-body">
        <div className="picture-card-title">
          <PlatformIcon platform="wordpress" />
          <button type="button" className="picture-card-link" title={family.name} onClick={onOpen}>
            {family.name}
          </button>
        </div>
        <div className="row-tight row-wrap">
          <span className="tag mono">{family.latest.version}</span>
          {family.latest.build ? null : <span className="tag">образ</span>}
          {family.outdatedCount > 0 ? <span className="tag tag-warning">{plural(family.outdatedCount, 'сайт', 'сайта', 'сайтов')} на старых версиях</span> : null}
        </div>
        <div className="picture-card-foot">
          <span>{plural(family.siteCount, 'сайт', 'сайта', 'сайтов')}</span>
          <span title={formatDateTime(family.latest.createdAt)}>{formatRelative(family.latest.createdAt)}</span>
        </div>
      </div>
    </article>
  );
}
